/**
 * career-compass-hub — the Kompass overview/landing (route `/compass`). A thin hub
 * that surfaces the four Kompass areas (Profil, Lebenslauf, Belegregister, Nachweise)
 * as nav cards with LIVE counts, so an agent (and a person) reads the durable
 * self-view's state at a glance. Fetch-projection; nav via real `<a>` + the `nav` verb.
 */

import {
  KitElement,
  signal,
  type ActionMap,
  type KitContext,
  type ReadonlySignal,
  type Signal,
  type ViewModel,
} from "@platform/web-kit"
import { html, type TemplateResult } from "@platform/web-kit/render"
import { I18nToken, RouterToken, apiGet } from "../services"
import type { CareerI18n, CareerKey } from "../../i18n"
import type { Router } from "@platform/web-kit"

interface HubCounts {
  hasNarrative: boolean
  cvVersions: number
  cvHasDraft: boolean
  evidenceOpen: number
  evidenceConfirmed: number
  assets: number
}

export class CompassHubVm implements ViewModel {
  readonly counts: Signal<HubCounts | null> = signal<HubCounts | null>(null)
  readonly renderDeps: ReadonlyArray<ReadonlySignal<unknown>>
  readonly t: CareerI18n["t"]

  constructor(private readonly router: Router, i18n: CareerI18n) {
    this.t = i18n.t
    this.renderDeps = [this.counts]
  }

  attach(): () => void {
    void this.refresh()
    return () => {}
  }

  /**
   * Each tile derives from its own read, INDEPENDENTLY — a single failing fetch
   * degrades only its own tile (leaves the last-known/zero) rather than zeroing the
   * whole hub. The fields are folded in as they resolve so a slow/failed one never
   * blocks the others.
   */
  private async refresh(): Promise<void> {
    const next: HubCounts = { hasNarrative: false, cvVersions: 0, cvHasDraft: false, evidenceOpen: 0, evidenceConfirmed: 0, assets: 0 }
    const tiles: Promise<void>[] = [
      apiGet<{ narrative: unknown | null }>("/api/profile")
        .then((p) => void (next.hasNarrative = p.narrative !== null))
        .catch(() => {}),
      apiGet<{ versions: unknown[] }>("/api/cv/versions")
        .then((v) => void (next.cvVersions = v.versions.length))
        .catch(() => {}),
      apiGet<{ cv: unknown | null; isDraft: boolean }>("/api/cv")
        .then((cv) => void (next.cvHasDraft = cv.cv !== null && cv.isDraft))
        .catch(() => {}),
      apiGet<{ status: string }[]>("/api/evidence")
        .then((e) => {
          next.evidenceOpen = e.filter((c) => c.status === "proposed").length
          next.evidenceConfirmed = e.filter((c) => c.status === "confirmed").length
        })
        .catch(() => {}),
      apiGet<unknown[]>("/api/assets")
        .then((a) => void (next.assets = a.length))
        .catch(() => {}),
    ]
    await Promise.all(tiles)
    this.counts.set(next)
  }

  go(url: string): void {
    void this.router.navigate(url)
  }
}

export class CareerCompassHub extends KitElement<CompassHubVm> {
  protected createViewModel(ctx: KitContext): CompassHubVm {
    return new CompassHubVm(ctx.inject(RouterToken), ctx.inject(I18nToken))
  }

  private card(vm: CompassHubVm, url: string, titleKey: CareerKey, descKey: CareerKey, badge: TemplateResult): TemplateResult {
    return html`
      <a class="compass-card" href=${url} data-act="nav" data-url=${url} lang="de">
        <h2 class="compass-card-title" lang="de">${vm.t(titleKey)}</h2>
        <p class="compass-card-desc" lang="de">${vm.t(descKey)}</p>
        <p class="compass-card-badge">${badge}</p>
      </a>
    `
  }

  protected render(vm: CompassHubVm): TemplateResult {
    const c = vm.counts.value ?? { hasNarrative: false, cvVersions: 0, cvHasDraft: false, evidenceOpen: 0, evidenceConfirmed: 0, assets: 0 }
    return html`
      <section class="surface compass-hub" aria-labelledby="compass-title">
        <h1 id="compass-title" class="surface-title" lang="de">${vm.t("surface.kompass.title")}</h1>
        <p class="surface-lead" lang="de">${vm.t("compass.lead")}</p>
        <div class="compass-grid">
          ${this.card(vm, "/compass/profile", "nav.profil", "compass.profile.desc",
            c.hasNarrative
              ? html`<span class="chip is-confirmed" lang="de">${vm.t("compass.profile.ready")}</span>`
              : html`<span class="chip is-gap" lang="de">${vm.t("compass.profile.pending")}</span>`)}
          ${this.card(vm, "/compass/cv", "nav.lebenslauf", "compass.cv.desc",
            html`<span class="chip" lang="de">${vm.t("compass.cv.versions", { n: c.cvVersions })}</span>${c.cvHasDraft ? html`<span class="chip is-draft" lang="de">${vm.t("compass.cv.draft")}</span>` : html``}`)}
          ${this.card(vm, "/compass/evidence", "nav.belege", "compass.evidence.desc",
            html`<span class="chip is-confirmed" lang="de">${vm.t("compass.evidence.confirmed", { n: c.evidenceConfirmed })}</span><span class="chip" lang="de">${vm.t("compass.evidence.open", { n: c.evidenceOpen })}</span>`)}
          ${this.card(vm, "/compass/assets", "nav.nachweise", "compass.assets.desc",
            html`<span class="chip" lang="de">${vm.t("compass.assets.count", { n: c.assets })}</span>`)}
        </div>
      </section>
    `
  }

  protected override actions(vm: CompassHubVm): ActionMap {
    return {
      nav: ({ url }, event) => {
        event.preventDefault()
        if (url) vm.go(url)
      },
    }
  }
}
